import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { useLocationId } from '@/data/useLocationId';
import { settingsApi } from '../api/settingsApi';
import type { SettingsOperatory } from '../api/types';

type OperatoriesQueryData = { operatories: SettingsOperatory[] };

function operatoryQueryKey(locationId: number, includeInactive: boolean) {
  return [...keys.settings.operatories(locationId), includeInactive] as const;
}

function patchOperatoriesCache(
  queryClient: QueryClient,
  locationId: number,
  updater: (operatories: SettingsOperatory[]) => SettingsOperatory[],
) {
  for (const includeInactive of [true, false] as const) {
    queryClient.setQueryData<OperatoriesQueryData>(
      operatoryQueryKey(locationId, includeInactive),
      (old) => {
        if (!old) return old;
        return { operatories: updater(old.operatories) };
      },
    );
  }
}

function invalidateOperatoryDependents(queryClient: QueryClient, locationId: number) {
  void queryClient.invalidateQueries({ queryKey: keys.settings.operatories(locationId) });
  void queryClient.invalidateQueries({ queryKey: ['workspace', 'bootstrap'] });
  void queryClient.invalidateQueries({ queryKey: ['calendar'] });
}

export function useOperatories(includeInactive = false) {
  const locationId = useLocationId();
  return useQuery({
    queryKey: operatoryQueryKey(locationId ?? 0, includeInactive),
    queryFn: ({ signal }) => settingsApi.getOperatories(locationId!, includeInactive, { signal }),
    enabled: locationId != null,
    staleTime: 300_000,
  });
}

export function useCreateOperatory() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SettingsOperatory>) => {
      if (locationId == null) throw new Error('Location required');
      return settingsApi.createOperatory(locationId, data);
    },
    onSuccess: (created) => {
      if (locationId == null) return;
      patchOperatoriesCache(queryClient, locationId, (operatories) => [...operatories, created]);
      invalidateOperatoryDependents(queryClient, locationId);
    },
  });
}

export function useUpdateOperatory() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SettingsOperatory> }) => {
      if (locationId == null) throw new Error('Location required');
      return settingsApi.updateOperatory(locationId, id, data);
    },
    onSuccess: (updated) => {
      if (locationId == null) return;
      patchOperatoriesCache(queryClient, locationId, (operatories) =>
        operatories.map((op) => (op.id === updated.id ? updated : op)),
      );
      invalidateOperatoryDependents(queryClient, locationId);
    },
  });
}

export function useDeleteOperatory() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (locationId == null) throw new Error('Location required');
      return settingsApi.deleteOperatory(locationId, id);
    },
    onSuccess: (_result, id) => {
      if (locationId == null) return;
      patchOperatoriesCache(queryClient, locationId, (operatories) =>
        operatories.filter((op) => op.id !== id),
      );
      invalidateOperatoryDependents(queryClient, locationId);
    },
  });
}
